import { LogOut, X } from "lucide-react";
import classNames from "classnames";
import { useEffect } from "react";
import { useScreenWidth } from "@/lib/useScreenWidth";
import Logo from "@/shared/components/Logo";
import { SidebarContext } from "./SidebarItem";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export default function Sidebar({ expanded, children, open, dispatch }) {
  const isSm = useScreenWidth({ width: 640 });

  useEffect(() => {
    if (isSm && open) {
      dispatch("TOGGLE_MOBILE");
    }
  }, [dispatch, isSm, open]);

  const { user, logOut } = useAuth();

  return (
    <aside
      className={classNames({
        "z-40 border-r dark:bg-dBackground bg-background min-h-screen flex flex-col h-dvh":
          true,
        "sm:block": isSm,
        hidden: !open,
        "absolute w-[100%]": open && !isSm,
        "w-[212px]": isSm && expanded,
        "w-[72px]": isSm && !expanded,
      })}
    >
      <nav className="flex flex-col h-full">
        <div
          className={cn({
            "p-4 h-[73px] border-b dark:border-neutral-800": true,
            "flex items-center justify-between": open,
          })}
        >
          <Logo expanded={expanded} />
          <button
            className="ml-4 h-[36px] sm:hidden rounded-lg hover:bg-secondary-200 hover:text-secondary-900"
            onClick={() => dispatch("TOGGLE_MOBILE")}
          >
            {open && <X className="h-6 w-6" />}
          </button>
        </div>

        <SidebarContext.Provider value={{ expanded, open }}>
          <ScrollArea
            className={classNames({
              "px-3 flex-1 overflow-y-auto": true,
            })}
          >
            <ul className="space-y-1 py-1">{children}</ul>
          </ScrollArea>
        </SidebarContext.Provider>
        {expanded && (
          <div className="border-t border-neutral-200 dark:border-neutral-800 mt-auto bg-neutral-50/50 dark:bg-neutral-900/50">
            <div className="transition-all duration-300">
              <div className="flex flex-col gap-1">
                <div className="p-3 rounded-lg bg-white dark:bg-neutral-800/50 shadow-sm">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">
                          {user?.email?.[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1 pl-10">
                      <p className="text-xs text-muted-foreground">
                        {user?.company?.name ?? "-"}
                      </p>
                      <p className="text-xs font-medium capitalize bg-primary/10 text-primary inline-block px-2 py-0.5 rounded-full">
                        {user?.role}
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={logOut}
                  variant="ghost"
                  className="w-full gap-2 text-primary hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </aside>
  );
}

export { Sidebar };
